import { useTheme } from '@mui/material'
import { useCallback, useMemo } from 'react'
import {
  IFormSchemaReport,
  IMoveReportDestinationPickerProps,
  formSchemaReportTypes,
} from 'src/generated-types/form-schema'
import { useDialogCtx } from 'src/hooks/context-hooks'
import MoveReportDialog from './MoveReportDialog'
import { MoveReportEquipmentPicker } from './MoveReportEquipmentPicker'
import { MoveReportWorkOrderPicker } from './MoveReportWorkOrderPicker'

interface IUseMoveReportDialogArgs {
  enabled: boolean
  customerId: EntityId
  type: formSchemaReportTypes
  mainComponentId?: string | EntityId
  scheduledTaskId?: number
  localityId?: EntityId
}

interface IMoveDestination {
  DestinationPicker: React.FC<IMoveReportDestinationPickerProps>
  excludedId: EntityId
}

export const useMoveReportDialog = ({
  enabled,
  customerId,
  type,
  mainComponentId,
  scheduledTaskId,
  localityId,
}: IUseMoveReportDialogArgs) => {
  const { showDialog } = useDialogCtx()
  const theme = useTheme()

  const destination = useMemo<IMoveDestination | undefined>(() => {
    if (!enabled) return undefined
    if (type === formSchemaReportTypes.MainComponent && mainComponentId != null) {
      return { DestinationPicker: MoveReportEquipmentPicker, excludedId: mainComponentId as EntityId }
    }
    if (type === formSchemaReportTypes.Task && scheduledTaskId != null) {
      return { DestinationPicker: MoveReportWorkOrderPicker, excludedId: scheduledTaskId as EntityId }
    }
    return undefined
  }, [enabled, type, mainComponentId, scheduledTaskId])

  const onMove = useCallback(
    (row: IFormSchemaReport) => {
      if (!destination) return
      showDialog(
        MoveReportDialog,
        {
          componentProps: {
            customerId,
            excludedId: destination.excludedId,
            localityId,
            report: row,
            DestinationPicker: destination.DestinationPicker,
          },
          opts: { maxWidth: 'sm', fullWidth: true },
          sx: {
            '& .MuiDialog-paper': { transition: 'max-width 200ms ease' },
            '& .MuiDialog-paper:has(> form)': { maxWidth: theme.breakpoints.values.lg },
          },
        },
        undefined,
        true,
      )
    },
    [customerId, localityId, destination, showDialog, theme],
  )

  return destination ? onMove : undefined
}
